import { useState } from 'react';
import type { ModuleConfig, LearnerState } from '@/lib/types';
import { SplitPanelLayout } from './SplitPanelLayout';
import { ChatPanel } from '../chat/ChatPanel';
import { ModuleNavigation } from './ModuleNavigation';
import { exportLearnerData } from '@/lib/api';

interface ModuleShellProps {
  config: ModuleConfig;
  state: LearnerState;
  learnerId: string;
  children: React.ReactNode;
}

export function ModuleShell({ config, state, learnerId, children }: ModuleShellProps) {
  const currentPart = config.parts[state.currentPosition.partIndex];
  const accentColor = currentPart.accentColor;

  // Navigation state
  const [isNavOpen, setIsNavOpen] = useState(true);
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    setIsExporting(true);
    try {
      await exportLearnerData(learnerId);
    } catch (error) {
      console.error('Failed to export data:', error);
    } finally {
      setIsExporting(false);
    }
  };

  // Chat is always available - user can open it anytime
  const showChatPanel = true;

  return (
    <div
      className="min-h-screen flex bg-slate-50 dark:bg-slate-900"
      style={{ '--color-accent': accentColor } as React.CSSProperties}
    >
      {/* Left Navigation */}
      <ModuleNavigation
        config={config}
        state={state}
        isOpen={isNavOpen}
        onToggle={() => setIsNavOpen(!isNavOpen)}
      />

      {/* Main area */}
      <div className={`flex-1 flex flex-col transition-all ${isNavOpen ? 'ml-80' : 'ml-0'}`}>
      {/* Header */}
      <header className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4 sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex-1">
            <h1 className="text-lg font-serif font-semibold text-slate-900 dark:text-slate-100 mb-1">
              {config.title}
            </h1>
            <div className="flex items-center gap-3 text-sm">
              <span className="text-slate-600 dark:text-slate-400">
                Part {state.currentPosition.partIndex + 1}: {currentPart.title}
              </span>
              <span className="text-slate-400 dark:text-slate-600">•</span>
              <span className="capitalize accent-text font-medium">
                {state.currentPosition.phase === 'content' && 'Content'}
                {state.currentPosition.phase === 'companion' && 'Companion'}
                {state.currentPosition.phase === 'assessment' && 'Assessment'}
                {state.currentPosition.phase === 'reflection' && 'Reflection'}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-4">
            {/* Export button */}
            <button
              onClick={handleExport}
              disabled={isExporting}
              title="Export learning data"
              className="p-2 rounded-lg text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors disabled:opacity-50"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
            </button>

            {/* Progress indicators */}
            <div className="flex gap-3">
              {config.parts.map((part, index) => {
                const isCompleted = index < state.currentPosition.partIndex;
                const isCurrent = index === state.currentPosition.partIndex;
                const isUpcoming = index > state.currentPosition.partIndex;

                return (
                  <div
                    key={part.id}
                    className="group relative"
                    title={part.title}
                  >
                    <div
                      className={`
                        progress-dot
                        ${isCompleted ? 'completed' : ''}
                        ${isCurrent ? 'current' : ''}
                        ${isUpcoming ? 'upcoming' : ''}
                      `}
                      style={
                        isCurrent || isCompleted
                          ? ({ '--color-accent': part.accentColor } as React.CSSProperties)
                          : {}
                      }
                    >
                      {isCompleted ? '✓' : index + 1}
                    </div>

                    {/* Tooltip on hover */}
                    <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2
                                    opacity-0 group-hover:opacity-100 transition-opacity
                                    bg-slate-900 dark:bg-slate-700 text-white text-xs
                                    rounded px-2 py-1 whitespace-nowrap pointer-events-none z-50">
                      {part.title}
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2
                                      border-4 border-transparent border-b-slate-900 dark:border-b-slate-700"></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </header>

        {/* Main content with split-panel layout - flex-1 so sidebar can share height */}
        <main className="flex-1 flex flex-col min-h-0 max-w-7xl w-full mx-auto px-6 py-8">
          <SplitPanelLayout
            mainContent={children}
            showSidePanel={showChatPanel}
            sidePanel={
              <ChatPanel
                learnerId={learnerId}
                moduleId={state.moduleId}
                state={state}
              />
            }
          />
        </main>
      </div>
    </div>
  );
}
